import React, { useCallback, useEffect, useState } from 'react';
import {
  FiRefreshCw, FiPlus, FiChevronLeft, FiAlertTriangle, FiClock, FiUserX, FiCalendar, FiCheck, FiX, FiSearch, FiTrash2,
} from 'react-icons/fi';
import { settlingAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './VisaCases.css';
import './Settling.css';
import { CheckInsPanel } from './FoundationStep3';

// Arrival & settling (brainstorms/2026-10-17-student-support-officer.md). Customer Support Officers
// (freelancers on the partner portal) settle clients from visa approval onward; this is the COO's
// side: who is arriving, who has no CSO, what is overdue, escalations, and CSO territories.

const KINDS = [
  { key: 'STUDENT', label: 'Student' },
  { key: 'JOB', label: 'Job seeker' },
  { key: 'TOURIST', label: 'Tourist' },
  { key: 'MEDICAL', label: 'Medical client' },
];
const INSTITUTION_LABEL = { STUDENT: 'University', JOB: 'Employer', MEDICAL: 'Hospital', TOURIST: 'Institution' };
const STATUS_TONE = { UPCOMING: 'vc-status-open', ARRIVED: 'vc-status-open', SETTLED: 'vc-status-approved', CLOSED: '', CANCELLED: 'vc-status-refused' };

const errText = (err, fallback) => {
  const d = err?.response?.data || {};
  return d.error || d.detail || Object.values(d).flat()[0] || fallback;
};
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const fmtDate = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '');
const where = (c) => [c.destination_city, c.destination_country].filter(Boolean).join(', ') || 'Destination not set';
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function CaseRow({ c, onOpen }) {
  return (
    <button type="button" className="vc-rowcard" onClick={() => onOpen(c.id)}>
      <div>
        <div><strong>{c.client_name}</strong> <span className="vc-muted">{c.client_ref} · {c.client_kind_display}</span></div>
        <div className="vc-muted">{where(c)} · {c.arrival_at ? `Arrives ${fmtDateTime(c.arrival_at)}` : 'Arrival not set'}</div>
      </div>
      <div className="vc-rowcard-side">
        <span className="vc-badges">
          {!c.assigned_sso && <span className="vc-badge vc-badge-bad"><FiUserX /> No CSO</span>}
          {c.assigned_sso && <span className="vc-badge vc-badge-info">{c.assigned_sso_name}</span>}
          {c.overdue_items > 0 && <span className="vc-badge vc-badge-bad"><FiClock /> {c.overdue_items} overdue</span>}
          {c.open_escalations > 0 && <span className="vc-badge vc-badge-warn"><FiAlertTriangle /> Escalated</span>}
        </span>
        <span className="vc-muted">{c.open_items} open</span>
        <span className={`vc-status ${STATUS_TONE[c.status] || ''}`}>{c.status_display}</span>
      </div>
    </button>
  );
}

function Section({ title, rows, empty, onOpen, tone }) {
  if (!rows) return null;
  return (
    <div className={`vc-panel ${tone || ''}`}>
      <div className="vc-panel-head"><h2>{title} <span className="vc-muted">({rows.length})</span></h2></div>
      {rows.length === 0 ? <div className="vc-muted">{empty}</div> : (
        <div className="vc-table">{rows.map((c) => <CaseRow key={c.id} c={c} onOpen={onOpen} />)}</div>
      )}
    </div>
  );
}

function Board({ onOpen, canManage }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    settlingAPI.board().then((r) => { setData(r.data); setError(''); })
      .catch((e) => setError(errText(e, 'Could not load the settling board.')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (noteId) => {
    try { await settlingAPI.resolveNote(noteId); load(); } catch (e) { setError(errText(e, 'Could not resolve.')); }
  };

  if (!data) return loading ? <div className="vc-empty">Loading…</div> : <div className="vc-error">{error}</div>;
  const k = data.counts;

  return (
    <>
      {error && <div className="vc-error">{error}</div>}
      <div className="vc-stats">
        <div className="vc-stat"><div className="vc-stat-value">{k.active}</div><div className="vc-stat-label">Active cases</div></div>
        <div className="vc-stat"><div className="vc-stat-value">{k.unassigned}</div><div className="vc-stat-label">Without an CSO</div></div>
        <div className="vc-stat"><div className="vc-stat-value">{k.arriving_soon}</div><div className="vc-stat-label">Arriving in 14 days</div></div>
        <div className="vc-stat"><div className="vc-stat-value">{k.overdue}</div><div className="vc-stat-label">With overdue items</div></div>
        <div className="vc-stat"><div className="vc-stat-value">{k.escalations}</div><div className="vc-stat-label">Open escalations</div></div>
      </div>

      {data.escalations.length > 0 && (
        <div className="vc-panel vc-risk">
          <div className="vc-panel-head"><h2><FiAlertTriangle /> Escalations</h2></div>
          <ul className="vc-risk-list">
            {data.escalations.map((n) => (
              <li key={n.id} className="st-escalation">
                <div>
                  <button type="button" className="vc-link" onClick={() => onOpen(n.case_id)}><strong>{n.client_name}</strong></button>
                  <span className="vc-muted"> · {n.author_name} · {fmtDateTime(n.created_at)}</span>
                  <div>{n.body}</div>
                </div>
                {canManage && <button type="button" className="vc-btn vc-btn-sm" onClick={() => resolve(n.id)}><FiCheck /> Resolved</button>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Section title="Without an CSO" rows={data.unassigned} onOpen={onOpen} tone={data.unassigned.length ? 'vc-risk' : ''}
        empty="Every active case has a Customer Support Officer." />
      <Section title="Arriving in the next 14 days" rows={data.arriving_soon} onOpen={onOpen} empty="No arrivals in the next two weeks." />
      <Section title="Overdue checklist items" rows={data.overdue} onOpen={onOpen} empty="Nothing overdue." />
      <Section title="Upcoming with no arrival date" rows={data.no_arrival_date} onOpen={onOpen} empty="Every upcoming case has an arrival date." />

      <div className="vc-panel">
        <div className="vc-panel-head">
          <h2>CSO caseload</h2>
          <button type="button" className="vc-btn vc-btn-sm" onClick={load} disabled={loading}><FiRefreshCw className={loading ? 'spin' : ''} /> Refresh</button>
        </div>
        {data.caseload.length === 0 ? <div className="vc-muted">No Customer Support Officers yet. Add one under the Officers tab.</div> : (
          <table className="st-table">
            <thead><tr><th>CSO</th><th>Active</th><th>Upcoming</th><th>With overdue items</th></tr></thead>
            <tbody>
              {data.caseload.map((r) => (
                <tr key={r.sso_id}><td>{r.sso_name}</td><td>{r.active}</td><td>{r.upcoming}</td><td>{r.overdue_cases || '—'}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function CaseList({ onOpen, officers }) {
  const [scope, setScope] = useState('active');
  const [kind, setKind] = useState('');
  const [sso, setSso] = useState('');
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = { scope, page_size: 100 };
    if (kind) params.kind = kind;
    if (sso) params.sso = sso;
    settlingAPI.list(params)
      .then((r) => { const d = r.data; setRows(d.results ?? d); setCount(d.count ?? (d.results ?? d).length); setError(''); })
      .catch((e) => setError(errText(e, 'Could not load cases.')))
      .finally(() => setLoading(false));
  }, [scope, kind, sso]);

  return (
    <>
      <div className="vc-filters">
        <div className="vc-tabs">
          {[['active', 'Active'], ['finished', 'Finished'], ['all', 'All']].map(([k, l]) => (
            <button key={k} type="button" className={scope === k ? 'active' : ''} onClick={() => setScope(k)}>{l}</button>
          ))}
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All client types</option>
          {KINDS.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </select>
        <select value={sso} onChange={(e) => setSso(e.target.value)}>
          <option value="">Every CSO</option>
          <option value="none">No CSO</option>
          {officers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <span className="vc-muted">{count} case{count === 1 ? '' : 's'}</span>
      </div>
      {error && <div className="vc-error">{error}</div>}
      {loading ? <div className="vc-empty">Loading…</div> : rows.length === 0 ? <div className="vc-empty">No cases.</div> : (
        <div className="vc-table">{rows.map((c) => <CaseRow key={c.id} c={c} onOpen={onOpen} />)}</div>
      )}
    </>
  );
}

function CaseDetail({ id, onBack, officers, canManage }) {
  const [c, setCase] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => settlingAPI.get(id).then((r) => setCase(r.data))
    .catch((e) => setError(errText(e, 'Could not load the case.'))), [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, fallback) => {
    setBusy(true); setError('');
    // Ticking an item or resolving a note answers with that item / note, not the case:
    // only a full case (with its checklist) may replace what is on screen.
    try { const r = await fn(); if (Array.isArray(r?.data?.items)) setCase(r.data); else await load(); }
    catch (e) { setError(errText(e, fallback)); }
    finally { setBusy(false); }
  };

  if (!c) return error ? <div className="vc-error">{error}</div> : <div className="vc-empty">Loading…</div>;
  const active = ['UPCOMING', 'ARRIVED', 'SETTLED'].includes(c.status);

  const startEdit = () => {
    setValues({
      destination_country: c.destination_country, destination_city: c.destination_city, institution: c.institution,
      arrival_at: toLocalInput(c.arrival_at), flight_number: c.flight_number, arrival_airport: c.arrival_airport,
      accommodation_address: c.accommodation_address,
    });
    setEditing(true);
  };
  const save = (e) => {
    e.preventDefault();
    run(async () => {
      const r = await settlingAPI.update(id, { ...values, arrival_at: values.arrival_at ? new Date(values.arrival_at).toISOString() : null });
      setEditing(false);
      return r;
    }, 'Could not save.');
  };
  const field = (name, label, props = {}) => (
    <label className="st-field">{label}
      <input value={values[name] ?? ''} onChange={(e) => setValues({ ...values, [name]: e.target.value })} {...props} />
    </label>
  );

  return (
    <div>
      <button type="button" className="vc-link vc-back" onClick={onBack}><FiChevronLeft /> Back</button>
      <div className="vc-detail-head">
        <div>
          <h2>{c.client_name}</h2>
          <div className="vc-muted">{c.client_ref} · {c.client_kind_display} · opened {fmtDate(c.opened_at)} ({c.opened_by_name})</div>
        </div>
        <div className="vc-detail-side">
          <span className={`vc-status ${STATUS_TONE[c.status] || ''}`}>{c.status_display}</span>
          <div className="st-actions">
            {active && c.status === 'UPCOMING' && (
              <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => run(() => settlingAPI.markArrived(id), 'Could not update.')}>
                <FiCalendar /> Mark arrived
              </button>
            )}
            {active && canManage && (
              <>
                <button type="button" className="vc-btn vc-btn-sm" disabled={busy}
                  onClick={() => window.confirm('Close this settling case?') && run(() => settlingAPI.finish(id, 'CLOSED'), 'Could not close.')}>
                  <FiCheck /> Close
                </button>
                <button type="button" className="vc-btn vc-btn-sm" disabled={busy}
                  onClick={() => window.confirm('Cancel this settling case?') && run(() => settlingAPI.finish(id, 'CANCELLED'), 'Could not cancel.')}>
                  <FiX /> Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {error && <div className="vc-error">{error}</div>}

      <div className="vc-panel">
        <div className="vc-panel-head">
          <h2>Customer Support Officer</h2>
        </div>
        {canManage && active ? (
          <div className="st-assign">
            <select value={c.assigned_sso || ''} disabled={busy}
              onChange={(e) => run(() => settlingAPI.assign(id, e.target.value || null), 'Could not assign.')}>
              <option value="">No CSO</option>
              {officers.filter((o) => o.is_active || o.id === c.assigned_sso).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}{o.territories.length ? ` (${o.territories.map((t) => t.city || t.country).join(', ')})` : ''}
                </option>
              ))}
            </select>
            {c.assigned_at && <span className="vc-muted">since {fmtDate(c.assigned_at)}</span>}
          </div>
        ) : (
          <div>{c.assigned_sso_name || <span className="vc-bad">No CSO assigned</span>}</div>
        )}
      </div>

      <div className="vc-panel">
        <div className="vc-panel-head">
          <h2>Destination & arrival</h2>
          {active && !editing && <button type="button" className="vc-btn vc-btn-sm" onClick={startEdit}>Edit</button>}
        </div>
        {editing ? (
          <form onSubmit={save}>
            <div className="st-grid">
              {field('destination_country', 'Destination country')}
              {field('destination_city', 'Destination city')}
              {field('institution', INSTITUTION_LABEL[c.client_kind])}
              {field('arrival_at', 'Arrival', { type: 'datetime-local' })}
              {field('flight_number', 'Flight number', { maxLength: 20 })}
              {field('arrival_airport', 'Arrival airport', { maxLength: 80 })}
              <label className="st-field st-wide">Accommodation address
                <textarea rows={2} value={values.accommodation_address ?? ''} onChange={(e) => setValues({ ...values, accommodation_address: e.target.value })} />
              </label>
            </div>
            <p className="vc-muted">Changing the city does not move the case to another CSO; reassign it above if needed.</p>
            <div className="st-actions">
              <button type="submit" className="vc-btn vc-btn-primary" disabled={busy}>Save</button>
              <button type="button" className="vc-btn" onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="st-facts">
            <div><span>Where</span>{where(c)}</div>
            <div><span>{INSTITUTION_LABEL[c.client_kind]}</span>{c.institution || '—'}</div>
            <div><span>Phone</span>{c.client_phone || '—'}</div>
            <div><span>Arrival</span>{fmtDateTime(c.arrival_at) || 'Not set'}</div>
            <div><span>Flight</span>{[c.flight_number, c.arrival_airport].filter(Boolean).join(' · ') || '—'}</div>
            <div><span>Accommodation</span>{c.accommodation_address || '—'}</div>
          </div>
        )}
      </div>

      <div className="vc-panel">
        <div className="vc-panel-head"><h2>Checklist</h2><span className="vc-muted">{c.open_items} open</span></div>
        <div className="st-items">
          {c.items.map((it) => (
            <div key={it.id} className="st-item">
              <input type="checkbox" checked={it.done} disabled={!active || busy || it.not_applicable}
                onChange={(e) => run(() => settlingAPI.updateItem(it.id, { done: e.target.checked }), 'Could not update.')} />
              <div className="st-item-body">
                <div className={it.not_applicable ? 'st-na' : ''}>{it.label}</div>
                {it.note && <div className="vc-muted">Note: {it.note}</div>}
                {it.done && <div className="vc-muted">Done {fmtDate(it.done_at)}{it.done_by_name ? ` by ${it.done_by_name}` : ''}</div>}
              </div>
              <div className="st-item-side">
                {!it.done && it.not_applicable && <span className="vc-badge">Not needed</span>}
                {!it.done && !it.not_applicable && it.is_overdue && <span className="vc-badge vc-badge-bad">Overdue · {fmtDate(it.due_date)}</span>}
                {!it.done && !it.not_applicable && !it.is_overdue && it.due_date && <span className="vc-badge vc-badge-info">Due {fmtDate(it.due_date)}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="vc-panel">
        <div className="vc-panel-head"><h2>Notes</h2></div>
        {c.notes.length === 0 && <div className="vc-muted">No notes yet.</div>}
        <div className="st-notes">
          {c.notes.map((n) => (
            <div key={n.id} className={`st-note ${n.is_escalation && !n.resolved_at ? 'st-note-esc' : ''}`}>
              <div className="vc-muted">
                <strong>{n.author_name}</strong>{n.author_is_sso ? ' (CSO)' : ''} · {fmtDateTime(n.created_at)}
                {n.is_escalation && (n.resolved_at
                  ? <span className="vc-badge vc-badge-ok" style={{ marginLeft: 6 }}>Escalation resolved by {n.resolved_by_name}</span>
                  : <span className="vc-badge vc-badge-warn" style={{ marginLeft: 6 }}>Escalation</span>)}
              </div>
              <div className="st-note-body">{n.body}</div>
              {n.is_escalation && !n.resolved_at && canManage && (
                <button type="button" className="vc-btn vc-btn-sm" onClick={() => run(() => settlingAPI.resolveNote(n.id), 'Could not resolve.')}>
                  <FiCheck /> Mark resolved
                </button>
              )}
            </div>
          ))}
        </div>
        <form className="st-note-form" onSubmit={(e) => {
          e.preventDefault();
          if (note.trim()) run(async () => { await settlingAPI.addNote(id, note.trim()); setNote(''); }, 'Could not add the note.');
        }}>
          <textarea rows={2} maxLength={4000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the CSO and the team" />
          <button type="submit" className="vc-btn vc-btn-primary" disabled={busy || !note.trim()}>Add note</button>
        </form>
      </div>
    </div>
  );
}

function OpenCaseModal({ onClose, onOpened }) {
  const [kind, setKind] = useState('STUDENT');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      settlingAPI.searchClients({ kind, q: q.trim() }).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [kind, q]);

  const open = async (client) => {
    try {
      const r = await settlingAPI.open(kind, client.id);
      onOpened(r.data.id);
    } catch (e) {
      const existing = e?.response?.data?.existing_case_id;
      if (existing) onOpened(existing); else setError(errText(e, 'Could not open the case.'));
    }
  };

  return (
    <div className="vc-overlay" role="dialog" aria-modal="true">
      <div className="vc-modal">
        <div className="vc-modal-head">
          <h2>Open a settling case</h2>
          <button type="button" className="vc-icon-btn" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>
        <p className="vc-muted">Cases open by themselves when a client's visa is approved. Use this for anyone who needs settling support earlier, or who arrived before this existed.</p>
        <div className="vc-tabs" style={{ margin: '10px 0' }}>
          {KINDS.map((x) => <button key={x.key} type="button" className={kind === x.key ? 'active' : ''} onClick={() => setKind(x.key)}>{x.label}</button>)}
        </div>
        <div className="vc-search"><FiSearch /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or reference" /></div>
        {error && <div className="vc-error">{error}</div>}
        <div className="vc-table" style={{ marginTop: 10 }}>
          {results.map((r) => (
            <button key={r.id} type="button" className="vc-rowcard" onClick={() => open(r)}>
              <span><strong>{r.name}</strong> <span className="vc-muted">{r.ref}</span></span>
              <span className="vc-muted">{r.has_active_case ? 'Has a case: open it' : 'Open case'}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Officers({ officers, reload, canManage }) {
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [profiles, setProfiles] = useState({});
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', username: '', email: '', phone: '', password: '' });

  const act = async (fn, fallback) => {
    setError('');
    try { await fn(); await reload(); } catch (e) { setError(errText(e, fallback)); }
  };

  const create = (e) => {
    e.preventDefault();
    act(async () => {
      await usersAPI.create({ ...form, role: 'CSO' });
      setForm({ first_name: '', last_name: '', username: '', email: '', phone: '', password: '' });
      setCreating(false);
    }, 'Could not create the account.');
  };

  return (
    <>
      {error && <div className="vc-error">{error}</div>}
      <div className="vc-panel">
        <div className="vc-panel-head">
          <h2>Customer Support Officers</h2>
          {canManage && !creating && <button type="button" className="vc-btn vc-btn-primary" onClick={() => setCreating(true)}><FiPlus /> New CSO</button>}
        </div>
        <p className="vc-muted">CSOs are freelancers. They sign in at partners.visionexploit.com and see only the clients assigned to them: name, phone, destination, arrival and the checklist. They never see documents, passports or payments. A signed data processing agreement must be in place before an CSO gets an account.</p>
        {creating && (
          <form className="st-create" onSubmit={create}>
            <div className="st-grid">
              {[['first_name', 'First name'], ['last_name', 'Last name'], ['username', 'Username'], ['email', 'Email', 'email'],
                ['phone', 'Phone'], ['password', 'Temporary password', 'password']].map(([n, l, type]) => (
                <label key={n} className="st-field">{l}
                  <input type={type || 'text'} required={n !== 'phone'} minLength={n === 'password' ? 8 : undefined}
                    value={form[n]} onChange={(e) => setForm({ ...form, [n]: e.target.value })} />
                </label>
              ))}
            </div>
            <div className="st-actions">
              <button type="submit" className="vc-btn vc-btn-primary">Create account</button>
              <button type="button" className="vc-btn" onClick={() => setCreating(false)}>Cancel</button>
            </div>
          </form>
        )}
      </div>

      {officers.length === 0 && <div className="vc-empty">No Customer Support Officers yet.</div>}
      {officers.map((o) => {
        const d = drafts[o.id] || { country: 'Turkey', city: '', is_primary: true };
        const p = profiles[o.id];
        return (
          <div key={o.id} className={`vc-panel ${o.is_active ? '' : 'st-inactive'}`}>
            <div className="vc-panel-head">
              <div>
                <h2>{o.name} {!o.is_active && <span className="vc-badge vc-badge-bad">Deactivated</span>}</h2>
                <div className="vc-muted">{o.username} · {o.email}{o.phone ? ` · ${o.phone}` : ''} · {o.active_cases} active case{o.active_cases === 1 ? '' : 's'}</div>
              </div>
              {canManage && (
                <button type="button" className="vc-btn vc-btn-sm"
                  onClick={() => window.confirm(o.is_active ? `Deactivate ${o.name}? They can no longer sign in; their open cases show as needing a new CSO.` : `Reactivate ${o.name}?`)
                    && act(() => usersAPI.update(o.id, { is_active: !o.is_active }), 'Could not update the account.')}>
                  {o.is_active ? 'Deactivate' : 'Reactivate'}
                </button>
              )}
            </div>

            <div className="st-subhead">Territories</div>
            {o.territories.length === 0 && <div className="vc-muted">No territory yet: no cases will be assigned automatically.</div>}
            <div className="st-territories">
              {o.territories.map((t) => (
                <span key={t.id} className={`st-territory ${t.is_active ? '' : 'st-na'}`}>
                  {t.city ? `${t.city}, ${t.country}` : `All of ${t.country}`}{t.is_primary ? '' : ' (backup)'}
                  {canManage && (
                    <>
                      <button type="button" className="vc-link" title={t.is_primary ? 'Make backup' : 'Make primary'}
                        onClick={() => act(() => settlingAPI.updateTerritory(t.id, { is_primary: !t.is_primary }), 'Could not update.')}>
                        {t.is_primary ? 'make backup' : 'make primary'}
                      </button>
                      <button type="button" className="vc-icon-btn st-del" aria-label="Remove territory"
                        onClick={() => window.confirm('Remove this territory?') && act(() => settlingAPI.deleteTerritory(t.id), 'Could not remove.')}>
                        <FiTrash2 />
                      </button>
                    </>
                  )}
                </span>
              ))}
            </div>
            {canManage && o.is_active && (
              <form className="st-add-territory" onSubmit={(e) => {
                e.preventDefault();
                act(async () => {
                  await settlingAPI.addTerritory({ sso: o.id, ...d });
                  setDrafts({ ...drafts, [o.id]: undefined });
                }, 'Could not add the territory.');
              }}>
                <input placeholder="Country" required value={d.country} onChange={(e) => setDrafts({ ...drafts, [o.id]: { ...d, country: e.target.value } })} />
                <input placeholder="City (blank = whole country)" value={d.city} onChange={(e) => setDrafts({ ...drafts, [o.id]: { ...d, city: e.target.value } })} />
                <label className="vc-check"><input type="checkbox" checked={d.is_primary} onChange={(e) => setDrafts({ ...drafts, [o.id]: { ...d, is_primary: e.target.checked } })} /> Primary</label>
                <button type="submit" className="vc-btn vc-btn-sm"><FiPlus /> Add territory</button>
              </form>
            )}

            <div className="st-subhead">Profile</div>
            {p ? (
              <form className="st-grid" onSubmit={(e) => {
                e.preventDefault();
                act(async () => { await settlingAPI.updateProfile(o.id, p); setProfiles({ ...profiles, [o.id]: undefined }); }, 'Could not save.');
              }}>
                <label className="st-field">Work WhatsApp<input value={p.whatsapp} onChange={(e) => setProfiles({ ...profiles, [o.id]: { ...p, whatsapp: e.target.value } })} /></label>
                <label className="st-field">Languages<input value={p.languages} placeholder="e.g. English, Turkish, Shona" onChange={(e) => setProfiles({ ...profiles, [o.id]: { ...p, languages: e.target.value } })} /></label>
                <label className="st-field st-wide">Internal notes (staff only)<textarea rows={2} value={p.internal_notes} onChange={(e) => setProfiles({ ...profiles, [o.id]: { ...p, internal_notes: e.target.value } })} /></label>
                <div className="st-actions"><button type="submit" className="vc-btn vc-btn-primary">Save</button>
                  <button type="button" className="vc-btn" onClick={() => setProfiles({ ...profiles, [o.id]: undefined })}>Cancel</button></div>
              </form>
            ) : (
              <div className="st-facts">
                <div><span>WhatsApp</span>{o.profile.whatsapp || '—'}</div>
                <div><span>Languages</span>{o.profile.languages || '—'}</div>
                {o.profile.internal_notes && <div><span>Internal notes</span>{o.profile.internal_notes}</div>}
                {canManage && <div><button type="button" className="vc-link" onClick={() => setProfiles({ ...profiles, [o.id]: { ...o.profile } })}>Edit profile</button></div>}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

export default function Settling() {
  const { isAdmin } = useAuth();
  const canManage = isAdmin();
  const [tab, setTab] = useState('board');
  const [caseId, setCaseId] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [opening, setOpening] = useState(false);

  const loadOfficers = useCallback(() => settlingAPI.officers()
    .then((r) => setOfficers(r.data.results ?? r.data)).catch(() => setOfficers([])), []);
  // Caseload counts change as cases are assigned, so refresh on every tab switch / return from a case.
  useEffect(() => { loadOfficers(); }, [loadOfficers, tab, caseId]);

  return (
    <div className="vc">
      <div className="vc-header">
        <div>
          <h1>Arrival & Settling</h1>
          <div className="vc-muted">Customer Support Officers settle clients on the ground from visa approval until they are settled.</div>
        </div>
        <div className="vc-header-actions">
          <button type="button" className="vc-btn vc-btn-primary" onClick={() => setOpening(true)}><FiPlus /> Open case</button>
        </div>
      </div>

      {caseId ? (
        <CaseDetail id={caseId} onBack={() => setCaseId(null)} officers={officers} canManage={canManage} />
      ) : (
        <>
          <div className="vc-tabs" style={{ marginTop: 16 }}>
            {[['board', 'Board'], ['cases', 'All cases'], ['checkins', 'Welfare check-ins'], ['officers', 'Officers & territories']].map(([k, l]) => (
              <button key={k} type="button" className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>
          {tab === 'board' && <Board onOpen={setCaseId} canManage={canManage} />}
          {tab === 'cases' && <CaseList onOpen={setCaseId} officers={officers} />}
          {tab === 'checkins' && <CheckInsPanel canManage={canManage} />}
          {tab === 'officers' && <Officers officers={officers} reload={loadOfficers} canManage={canManage} />}
        </>
      )}

      {opening && <OpenCaseModal onClose={() => setOpening(false)} onOpened={(id) => { setOpening(false); setCaseId(id); }} />}
    </div>
  );
}
